<template>
    <div class="orders-detail container">
        <div class="orders-detail__page-header d-flex justify-content-between align-items-center">
            <div>
                <h1>{{ $t('views.orders.detail.title') }}</h1>
            </div>
        </div>

        <div class="row">
            <div class="col-xs-12 col-lg-9">
                <template v-for="orderDetail in detail.items" :key="orderDetail.product.detail_id">
                    <div class="row g-0">
                        <div class="col" style="max-width: 122px; margin-right: 30px; background-color: #fff">
                            <ImagePlaceholder :img_url="orderDetail.product?.img_url" width="122" height="122" style="border-radius: 5px; border: 0.6px solid #e7eaf3; object-fit: contain; padding: 10px; margin-bottom: 30px" />
                        </div>
                        <div class="col">
                            <div class="card">
                                <table class="table table-borderless table-align-middle table-thead-bordered sm-d-block" style="overflow-x: auto">
                                    <thead class="thead-light">
                                        <tr>
                                            <th>{{ $t('views.orders.detail.columns.productInfo') }}</th>
                                            <th>{{ $t('views.orders.detail.columns.quantity_ordered') }}</th>
                                            <th>{{ $t('views.orders.detail.columns.sell_price') }}</th>
                                            <th>{{ $t('views.orders.columns.cog') }}</th>
                                            <th>{{ $t('views.orders.detail.columns.status') }}</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td style="min-width: 398px !important">
                                                <div class="d-flex flex-column">
                                                    <Popper class="popp" hover arrow>
                                                        <template #content>{{ orderDetail.product?.title }}</template>
                                                        <div class="text-ellipsis title" style="font-size: 11.9px; font-weight: 400; line-height: 17.9px">
                                                            {{ orderDetail.product?.title }}
                                                        </div>
                                                    </Popper>
                                                    <div class="d-flex align-items-center" style="gap: 5px">
                                                        <span style="font-size: 11.9px; font-weight: 400; line-height: 17.9px">ASIN:</span>
                                                        <InventoryClipboard :asin="orderDetail.product?.asin" style="font-size: 11.9px" />
                                                    </div>
                                                    <div class="d-flex align-items-center" style="gap: 5px">
                                                        <span style="font-size: 11.9px; font-weight: 400; line-height: 17.9px">SKU:</span>
                                                        <InventoryClipboard type="sku" :asin="orderDetail.product?.asin" :sku="orderDetail.product?.sku" style="font-size: 11.9px" />
                                                    </div>
                                                    <div v-if="orderDetail.product?.is_returned && orderDetail.return?.detailed_disposition" class="d-flex align-items-center" style="gap: 5px">
                                                        <span style="font-size: 11.9px; font-weight: 400; line-height: 17.9px">{{ $t('views.orders.detail.returnColumn.disposition') }}:</span>
                                                        <span :class="orderDetail.return?.detailed_disposition === 'SELLABLE' ? 'text-success' : 'text-danger'" style="font-size: 11.9px; font-weight: 600; line-height: 17.9px">{{
                                                            $t('views.orders.detail.returnDisposition.' + orderDetail.return?.detailed_disposition)
                                                        }}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{{ orderDetail.product?.quantity_ordered }}</td>
                                            <td>
                                                <div class="d-flex align-items-center" style="gap: 5px">
                                                    <span style="font-size: 11.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ orderDetail.calculated?.currency }}</span>
                                                    <span style="font-size: 11.9px; font-weight: 700; line-height: 17.85px; color: #00203f">{{ formatNumber(orderDetail.calculated?.item_price) }}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div class="d-flex align-items-center" v-if="orderDetail.calculated?.sell_price > 0" style="gap: 5px; width: 100px">
                                                    <span>{{ orderDetail.calculated?.sourceCurrency }}</span>
                                                    <CurrencyInput
                                                        v-model="orderDetail.calculated.cost_of_goods_unit"
                                                        :currency="orderDetail.calculated?.sourceCurrency || 'USD'"
                                                        class="form-control form-control-sm"
                                                        style="min-height: 14px !important; padding: 2px 2px 2px 5px !important; font-weight: 600; font-size: 0.8rem"
                                                        @focus="() => (storeCOG = orderDetail.calculated.cost_of_goods_unit)"
                                                        @blur="updateCost(detail.order?.id, orderDetail.product.detail_id, orderDetail.calculated.cost_of_goods_unit, detail.order.id)"
                                                    />
                                                </div>
                                                <template v-else>-</template>
                                            </td>
                                            <td>
                                                <div class="d-flex flex-column align-items-start">
                                                    <div class="status" :class="orderDetail.product?.is_returned ? 'Refunded' : detail.statuses?.order_status">
                                                        {{ getStatusLabel(orderDetail.product?.is_returned ? 'Refunded' : detail.statuses?.order_status) }}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <button
                                                    v-if="detail.items.length > 1"
                                                    class="btn btn-link"
                                                    :class="selectedItem === orderDetail.product.detail_id ? 'text-primary' : 'text-secondary'"
                                                    @click="handleChangeCurrentCalculated(orderDetail.product.detail_id)"
                                                    style="padding: 0; font-size: 25px"
                                                >
                                                    <i class="bi bi-receipt"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </template>
                <div class="card">
                    <div class="card-header d-flex align-items-center">{{ $t('views.orders.detail.orderInformation.title') }}</div>
                    <div class="card-body" style="padding: 40px">
                        <div class="row">
                            <div :class="detail.statuses?.is_returned && detail.return?.return_date ? 'col-4' : 'col-6'">
                                <div class="order-detail-item d-flex flex-column">
                                    <span>{{ $t('views.orders.detail.orderInformation.purchase_date') }}</span>
                                    <span>{{ moment(detail.order?.purchase_date).format('DD.MM.YYYY HH:mm') }}</span>
                                </div>
                                <div class="order-detail-item d-flex flex-column">
                                    <span>{{ $t('views.orders.detail.orderInformation.shipping_date') }}</span>
                                    <span>{{ moment(detail.order?.shipping_date).format('DD.MM.YYYY HH:mm') }}</span>
                                </div>
                            </div>
                            <div :class="detail.statuses?.is_returned && detail.return?.return_date ? 'col-4' : 'col-6'">
                                <div class="order-detail-item d-flex flex-column">
                                    <span>{{ $t('views.orders.detail.orderInformation.orderId') }}</span>
                                    <span>
                                        {{ detail.order?.id }}
                                        <a :href="`https://sellercentral.amazon.com/orders-v3/order/${detail.order?.id}`" class="btn btn-link" target="_blank" style="padding: 0; margin-top: -4px">
                                            <i class="bi bi-box-arrow-up-right"></i>
                                        </a>
                                    </span>
                                </div>
                                <!-- <div class="order-detail-item d-flex flex-column">
                                    <span>İsim</span>
                                    <span>{{ detail.buyer?.name }}</span>
                                </div> -->
                                <div class="order-detail-item d-flex flex-column">
                                    <span>{{ $t('views.orders.detail.orderInformation.address') }}</span>
                                    <span>{{ buyerAddress }}</span>
                                </div>
                            </div>
                            <div v-if="detail.statuses?.is_returned && detail.return?.return_date" class="col-4">
                                <div class="order-detail-item d-flex flex-column">
                                    <span>{{ $t('views.orders.detail.orderInformation.return_date') }}</span>
                                    <span>{{ moment(detail.return?.return_date).format('DD.MM.YYYY') }}</span>
                                </div>
                                <div class="order-detail-item d-flex flex-column">
                                    <span>{{ $t('views.orders.detail.orderInformation.return_comment') }}</span>
                                    <span>{{ detail.return?.customer_comments !== '' ? detail.return?.customer_comments : '-' }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card mt-5 mb-5">
                    <div class="card-header d-flex align-items-center">{{ $t('views.orders.detail.orderNote.title') }}</div>
                    <div class="card-body" style="padding: 40px">
                        <textarea v-model="order_note" class="form-control" :placeholder="$t('views.orders.detail.orderNote.placeholder')" style="height: 140px"></textarea>
                    </div>
                    <div class="card-footer" style="align-self: flex-end">
                        <button class="btn btn-primary btn-sm" @click="setOrderNote({ id: detail.order?.id, order_note })">{{ $t('views.orders.detail.orderNote.submitButton') }}</button>
                    </div>
                </div>
            </div>
            <div class="col-xs-12 col-lg-3">
                <div class="card">
                    <div class="card-header d-flex" style="justify-content: space-between">
                        <div class="d-flex flex-column">
                            <span v-if="(detail.order?.replaced_to && detail.order?.replaced_to.length > 0) || detail.order?.main_order">{{ $t(`views.orders.detail.salesSummary.replacementTitle`) }}</span>
                            <span v-else-if="detail.order?.replaced_to.length === 0 && detail.statuses?.is_returned">{{ $t(`views.orders.detail.salesSummary.refundTitle`) }}</span>
                            <span v-else-if="!detail.statuses?.is_returned">{{
                                !['Pending', 'PendingAvailibility'].includes(detail.statuses?.order_status) ? $t(`views.orders.detail.salesSummary.title`) : $t(`views.orders.detail.salesSummary.pendingTitle`)
                            }}</span>
                            <span v-if="detail.order?.replaced_to && detail.order?.replaced_to.length > 0" style="font-size: 12px; font-weight: 400"
                                >{{ $t('views.orders.detail.salesSummary.replacementSubtitle') }}
                                <div class="d-flex" v-for="id of detail.order?.replaced_to" :key="id">
                                    <router-link target="_blank" :active-class="'active'" :to="'/orders/' + id">{{ id }}</router-link>
                                </div>
                            </span>
                            <span v-if="detail.order?.main_order" style="font-size: 12px; font-weight: 400"
                                >{{ $t('views.orders.detail.salesSummary.mainSubtitle') }} <router-link target="_blank" :active-class="'active'" :to="'/orders/' + detail.order.main_order">{{ detail.order.main_order }}</router-link></span
                            >
                        </div>
                        <span v-if="selectedSku" style="font-size: 12px; color: #677788">{{ selectedSku }}</span>
                    </div>
                    <div class="card-body">
                        <div class="d-flex" style="border-bottom: 0; border-color: transparent; justify-content: space-between">
                            <div style="width: 125px; font-size: 14px; font-weight: 400; line-height: 21px; white-space: nowrap">
                                <button class="btn btn-link text-secondary" style="padding: 0; font-size: 16px; font-weight: bold" @click="hiddenFields.sell_price = !hiddenFields.sell_price">
                                    <i class="bi" :class="hiddenFields.sell_price ? 'bi-dash' : 'bi-plus'"></i>
                                </button>
                                {{ $t(`views.orders.detail.salesSummary.columns.sell_price`) }}
                            </div>
                            <div class="d-flex align-items-center" style="gap: 5px">
                                <span style="font-size: 11.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.currency }}</span>
                                <span style="font-size: 11.9px; font-weight: 700; line-height: 17.85px; color: #00203f" :style="currentCalculated?.sell_price === 0 && 'opacity:0.5'"> {{ formatNumber(currentCalculated?.total_charges) }}</span>
                            </div>
                        </div>
                        <div v-if="hiddenFields.sell_price">
                            <template
                                v-for="item in Object.keys(currentCalculated.charges_data).sort((a, b) => {
                                    if (a === 'Principal') return -1
                                    if (b === 'Principal') return 1
                                    if (a === 'Tax') return -1
                                    if (b === 'Tax') return 1

                                    // Tax içeren keyleri ana keylerinin hemen ardına yerleştir
                                    const aTax = a.endsWith('Tax')
                                    const bTax = b.endsWith('Tax')
                                    if (aTax !== bTax) {
                                        if (aTax && b === a.replace('Tax', '')) return 1
                                        if (bTax && a === b.replace('Tax', '')) return -1
                                    }

                                    return a.localeCompare(b)
                                })"
                                :key="item"
                            >
                                <div class="d-flex" style="margin-top: 5px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                                    <div style="width: 125px; font-size: 12px; font-weight: 400; line-height: 21px; white-space: nowrap; padding-left: 22px">
                                        {{ $t(`views.orders.financeLabels.` + item) }}
                                    </div>
                                    <div class="d-flex align-items-center" style="gap: 5px">
                                        <span style="font-size: 10.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.currency }}</span>
                                        <span style="font-size: 10.9px; font-weight: 600; line-height: 17.85px; color: #00203f" :style="currentCalculated.charges_data[item] === 0 && 'opacity:0.5'">
                                            {{ formatNumber(currentCalculated.charges_data[item]) }}</span
                                        >
                                    </div>
                                </div>
                            </template>
                        </div>
                        <div class="d-flex" style="margin-top: 15px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                            <div style="width: 125px; font-size: 14px; font-weight: 400; line-height: 21px; white-space: nowrap">
                                <button class="btn btn-link text-secondary" style="padding: 0; font-size: 16px; font-weight: bold" @click="hiddenFields.fees = !hiddenFields.fees">
                                    <i class="bi" :class="hiddenFields.fees ? 'bi-dash' : 'bi-plus'"></i>
                                </button>
                                {{ $t(`views.orders.detail.salesSummary.columns.fees`) }}
                            </div>
                            <div class="d-flex align-items-center" style="gap: 5px">
                                <span style="font-size: 11.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.currency }}</span>
                                <span
                                    style="font-size: 11.9px; font-weight: 700; line-height: 17.85px; color: #00203f"
                                    :class="currentCalculated?.total_fees < 0 ? 'text-danger' : 'text-secondary'"
                                    :style="currentCalculated?.total_fees === 0 && 'opacity:0.5'"
                                >
                                    {{ formatNumber(currentCalculated?.total_fees) }}</span
                                >
                            </div>
                        </div>
                        <div v-if="hiddenFields.fees">
                            <template v-for="item in Object.keys(currentCalculated.fees_data).sort()" :key="item">
                                <div v-if="currentCalculated.fees_data[item] !== 0" class="d-flex" style="margin-top: 5px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                                    <div style="width: 125px; font-size: 12px; font-weight: 400; line-height: 21px; white-space: nowrap; padding-left: 22px">
                                        {{ $t(`views.orders.financeLabels.` + item) }}
                                    </div>
                                    <div class="d-flex align-items-center" style="gap: 5px">
                                        <span style="font-size: 10.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.currency }}</span>
                                        <span
                                            style="font-size: 10.9px; font-weight: 600; line-height: 17.85px; color: #00203f"
                                            :class="currentCalculated.fees_data[item] < 0 ? 'text-danger' : 'text-secondary'"
                                            :style="currentCalculated.fees_data[item] === 0 && 'opacity:0.5'"
                                        >
                                            {{ formatNumber(currentCalculated.fees_data[item]) }}</span
                                        >
                                    </div>
                                </div>
                            </template>
                        </div>
                        <div v-if="currentCalculated?.total_promotions !== 0" class="d-flex" style="margin-top: 15px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                            <div style="width: 125px; font-size: 14px; font-weight: 400; line-height: 21px; white-space: nowrap">
                                <button class="btn btn-link text-secondary" style="padding: 0; font-size: 16px; font-weight: bold" @click="hiddenFields.promos = !hiddenFields.promos">
                                    <i class="bi" :class="hiddenFields.promos ? 'bi-dash' : 'bi-plus'"></i>
                                </button>
                                {{ $t(`views.orders.detail.salesSummary.columns.promos`) }}
                            </div>
                            <div class="d-flex align-items-center" style="gap: 5px">
                                <span style="font-size: 11.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.currency }}</span>
                                <span
                                    style="font-size: 11.9px; font-weight: 700; line-height: 17.85px; color: #00203f"
                                    :class="currentCalculated?.total_promotions < 0 ? 'text-danger' : 'text-secondary'"
                                    :style="currentCalculated?.total_promotions === 0 && 'opacity:0.5'"
                                >
                                    {{ formatNumber(currentCalculated?.total_promotions) }}</span
                                >
                            </div>
                        </div>
                        <div v-if="hiddenFields.promos">
                            <template v-for="item in Object.keys(currentCalculated.promotion_data).sort()" :key="item">
                                <div class="d-flex" style="margin-top: 5px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                                    <div style="width: 125px; font-size: 12px; font-weight: 400; line-height: 21px; white-space: nowrap; padding-left: 22px">
                                        {{ $t(`views.orders.financeLabels.` + item) }}
                                    </div>
                                    <div class="d-flex align-items-center" style="gap: 5px">
                                        <span style="font-size: 10.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.currency }}</span>
                                        <span
                                            style="font-size: 10.9px; font-weight: 600; line-height: 17.85px; color: #00203f"
                                            :class="currentCalculated.promotion_data[item] < 0 ? 'text-danger' : 'text-secondary'"
                                            :style="currentCalculated.promotion_data[item] === 0 && 'opacity:0.5'"
                                        >
                                            {{ formatNumber(currentCalculated.promotion_data[item]) }}</span
                                        >
                                    </div>
                                </div>
                            </template>
                        </div>
                        <div v-if="currentCalculated?.total_taxwitheld !== 0" class="d-flex" style="margin-top: 15px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                            <div style="padding-left: 22px; width: 125px; font-size: 14px; font-weight: 400; line-height: 21px; white-space: nowrap">
                                {{ $t(`views.orders.detail.salesSummary.columns.taxWithheld`) }}
                            </div>
                            <div class="d-flex align-items-center" style="gap: 5px">
                                <span style="font-size: 11.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.currency }}</span>
                                <span
                                    style="font-size: 11.9px; font-weight: 700; line-height: 17.85px; color: #00203f"
                                    :class="currentCalculated?.total_taxwitheld === 0 ? 'text-secondary' : currentCalculated?.total_taxwitheld > 0 ? 'text-secondary' : 'text-danger'"
                                    :style="currentCalculated?.total_taxwitheld === 0 && 'opacity:0.5'"
                                >
                                    {{ formatNumber(currentCalculated?.total_taxwitheld) }}</span
                                >
                            </div>
                        </div>
                        <div v-if="ccurrentCalculated?.revenue_before_refund !== 0 && detail.statuses?.is_returned" class="d-flex" style="margin-top: 15px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                            <div style="padding-left: 22px; width: 125px; font-size: 14px; font-weight: 400; line-height: 21px; white-space: nowrap">
                                {{ $t(`views.orders.detail.salesSummary.columns.revenue_before_refund`) }}
                            </div>
                            <div class="d-flex align-items-center" style="gap: 5px">
                                <span style="font-size: 11.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.currency }}</span>
                                <span
                                    style="font-size: 11.9px; font-weight: 700; line-height: 17.85px; color: #00203f"
                                    :class="currentCalculated?.revenue_before_refund === 0 ? 'text-secondary' : currentCalculated?.revenue_before_refund > 0 ? 'text-success' : 'text-danger'"
                                    :style="currentCalculated?.revenue_before_refund === 0 && 'opacity:0.5'"
                                >
                                    {{ formatNumber(currentCalculated?.revenue_before_refund) }}</span
                                >
                            </div>
                        </div>
                        <div v-if="ccurrentCalculated?.revenue !== 0 && !detail.statuses?.is_returned" class="d-flex" style="margin-top: 15px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                            <div style="padding-left: 22px; width: 125px; font-size: 14px; font-weight: 400; line-height: 21px; white-space: nowrap">
                                {{ $t(`views.orders.detail.salesSummary.columns.recieved_price`) }}
                            </div>
                            <div class="d-flex align-items-center" style="gap: 5px">
                                <span style="font-size: 11.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.currency }}</span>
                                <span
                                    style="font-size: 11.9px; font-weight: 700; line-height: 17.85px; color: #00203f"
                                    :class="currentCalculated?.revenue === 0 ? 'text-secondary' : currentCalculated?.revenue > 0 ? 'text-success' : 'text-danger'"
                                    :style="currentCalculated?.revenue === 0 && 'opacity:0.5'"
                                >
                                    {{ formatNumber(currentCalculated?.revenue) }}</span
                                >
                            </div>
                        </div>
                        <div v-if="currentCalculated?.cost_of_goods !== 0" class="d-flex" style="margin-top: 15px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                            <div style="padding-left: 22px; width: 125px; font-size: 14px; font-weight: 400; line-height: 21px; white-space: nowrap">
                                {{ $t(`views.orders.detail.salesSummary.columns.cost_of_goods`) }}
                            </div>
                            <div class="d-flex align-items-center" style="gap: 5px">
                                <span style="font-size: 11.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.sourceCurrency || currentCalculated?.currency }}</span>
                                <span
                                    style="font-size: 11.9px; font-weight: 700; line-height: 17.85px; color: #00203f"
                                    :class="currentCalculated?.cost_of_goods === 0 ? 'text-secondary' : currentCalculated?.cost_of_goods > 0 ? 'text-danger' : 'text-danger'"
                                    :style="currentCalculated?.cost_of_goods === 0 && 'opacity:0.5'"
                                >
                                    -{{ formatNumber(currentCalculated?.cost_of_goods) }}</span
                                >
                            </div>
                        </div>
                        <div v-if="ccurrentCalculated?.profit_before_refund !== 0 && detail.statuses?.is_returned" class="d-flex" style="margin-top: 15px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                            <div style="padding-left: 22px; width: 125px; font-size: 14px; font-weight: 400; line-height: 21px; white-space: nowrap">
                                {{ $t(`views.orders.detail.salesSummary.columns.profit_before_refund`) }}
                            </div>
                            <div class="d-flex align-items-center" style="gap: 5px">
                                <span style="font-size: 11.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.currency }}</span>
                                <span
                                    style="font-size: 11.9px; font-weight: 700; line-height: 17.85px; color: #00203f"
                                    :class="currentCalculated?.profit_before_refund === 0 ? 'text-secondary' : currentCalculated?.profit_before_refund > 0 ? 'text-success' : 'text-danger'"
                                    :style="currentCalculated?.profit_before_refund === 0 && 'opacity:0.5'"
                                >
                                    {{ formatNumber(currentCalculated?.profit_before_refund) }}</span
                                >
                            </div>
                        </div>
                        <div v-if="ccurrentCalculated?.roi_before_refund !== 0 && detail.statuses?.is_returned" class="d-flex" style="margin-top: 15px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                            <div style="padding-left: 22px; width: 125px; font-size: 14px; font-weight: 400; line-height: 21px; white-space: nowrap">
                                {{ $t(`views.orders.detail.salesSummary.columns.roi_before_refund_` + (currentCalculated?.profit_before_refund > 0 ? 'profit' : 'loss')) }}
                            </div>
                            <div class="d-flex align-items-right" :class="currentCalculated?.roi_before_refund === 0 ? 'text-secondary' : currentCalculated?.roi_before_refund > 0 ? 'text-success' : 'text-danger'" style="gap: 5px">
                                <i :class="currentCalculated?.roi_before_refund > 0 ? 'bi-graph-up' : 'bi-graph-down'"></i>
                                <span style="font-size: 11.9px; font-weight: 700; line-height: 21.85px">{{ formatPercentage(currentCalculated?.roi_before_refund) }}</span>
                            </div>
                        </div>
                        <div v-if="currentCalculated?.price_before_refund && detail.statuses?.is_returned" class="d-flex" style="margin-top: 15px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                            <div style="padding-left: 22px; width: 125px; font-size: 14px; font-weight: 400; line-height: 21px; white-space: nowrap">
                                {{ $t(`views.orders.detail.salesSummary.columns.price_before_refund`) }}
                            </div>
                            <div class="d-flex align-items-center" style="gap: 5px">
                                <span style="font-size: 11.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.sourceCurrency || currentCalculated?.currency }}</span>
                                <span
                                    style="font-size: 11.9px; font-weight: 700; line-height: 17.85px; color: #00203f"
                                    :class="currentCalculated?.price_before_refund < 0 ? 'text-danger' : 'text-secondary'"
                                    :style="currentCalculated?.price_before_refund === 0 && 'opacity:0.5'"
                                >
                                    {{ formatNumber(currentCalculated?.price_before_refund) }}</span
                                >
                            </div>
                        </div>
                        <hr v-if="currentCalculated?.total_refund !== 0" />
                        <div v-if="currentCalculated?.total_charge_adjustments !== 0" class="d-flex" style="margin-top: 15px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                            <div style="width: 125px; font-size: 14px; font-weight: 400; line-height: 21px; white-space: nowrap">
                                <button
                                    v-if="currentCalculated?.total_charge_adjustments !== 0"
                                    class="btn btn-link text-secondary"
                                    style="padding: 0; font-size: 16px; font-weight: bold"
                                    @click="hiddenFields.refund_price_total = !hiddenFields.refund_price_total"
                                >
                                    <i class="bi" :class="hiddenFields.refund_price_total ? 'bi-dash' : 'bi-plus'"></i>
                                </button>
                                {{ $t(`views.orders.detail.salesSummary.columns.refund_price_total`) }}
                            </div>
                            <div class="d-flex align-items-center" style="gap: 5px">
                                <span style="font-size: 11.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.currency }}</span>
                                <span
                                    style="font-size: 11.9px; font-weight: 700; line-height: 17.85px; color: #00203f"
                                    :class="currentCalculated?.total_charge_adjustments < 0 ? 'text-danger' : 'text-secondary'"
                                    :style="currentCalculated?.total_charge_adjustments === 0 && 'opacity:0.5'"
                                >
                                    {{ formatNumber(currentCalculated?.total_charge_adjustments) }}</span
                                >
                            </div>
                        </div>
                        <div v-if="hiddenFields.refund_price_total">
                            <template v-for="item in Object.keys(currentCalculated.charge_adjustments_data).sort()" :key="item">
                                <div class="d-flex" style="margin-top: 5px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                                    <div style="width: 125px; font-size: 12px; font-weight: 400; line-height: 21px; white-space: nowrap; padding-left: 22px">
                                        {{ $t(`views.orders.financeLabels.` + item) }}
                                    </div>
                                    <div class="d-flex align-items-center" style="gap: 5px">
                                        <span style="font-size: 10.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.currency }}</span>
                                        <span
                                            style="font-size: 10.9px; font-weight: 600; line-height: 17.85px; color: #00203f"
                                            :class="currentCalculated.charge_adjustments_data[item] < 0 ? 'text-danger' : 'text-secondary'"
                                            :style="currentCalculated.charge_adjustments_data[item] === 0 && 'opacity:0.5'"
                                        >
                                            {{ formatNumber(currentCalculated.charge_adjustments_data[item]) }}</span
                                        >
                                    </div>
                                </div>
                            </template>
                        </div>
                        <div v-if="currentCalculated?.total_fee_adjustments !== 0" class="d-flex" style="margin-top: 15px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                            <div style="width: 125px; font-size: 14px; font-weight: 400; line-height: 21px; white-space: nowrap">
                                <button class="btn btn-link text-secondary" style="padding: 0; font-size: 16px; font-weight: bold" @click="hiddenFields.total_refunded_fee = !hiddenFields.total_refunded_fee">
                                    <i class="bi" :class="hiddenFields.total_refunded_fee ? 'bi-dash' : 'bi-plus'"></i>
                                </button>
                                {{ $t(`views.orders.detail.salesSummary.columns.total_refunded_fee`) }}
                            </div>
                            <div class="d-flex align-items-center" style="gap: 5px">
                                <span style="font-size: 11.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.currency }}</span>
                                <span
                                    style="font-size: 11.9px; font-weight: 700; line-height: 17.85px; color: #00203f"
                                    :class="currentCalculated?.total_fee_adjustments < 0 ? 'text-danger' : 'text-secondary'"
                                    :style="currentCalculated?.total_fee_adjustments === 0 && 'opacity:0.5'"
                                >
                                    {{ formatNumber(currentCalculated?.total_fee_adjustments) }}</span
                                >
                            </div>
                        </div>
                        <div v-if="hiddenFields.total_refunded_fee">
                            <template v-for="item in Object.keys(currentCalculated.fee_adjustments_data).sort()" :key="item">
                                <div class="d-flex" style="margin-top: 5px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                                    <div style="width: 125px; font-size: 12px; font-weight: 400; line-height: 21px; white-space: nowrap; padding-left: 22px">
                                        {{ $t(`views.orders.financeLabels.` + item) }}
                                    </div>
                                    <div class="d-flex align-items-center" style="gap: 5px">
                                        <span style="font-size: 10.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.currency }}</span>
                                        <span
                                            style="font-size: 10.9px; font-weight: 600; line-height: 17.85px; color: #00203f"
                                            :class="currentCalculated.fee_adjustments_data[item] < 0 ? 'text-danger' : 'text-secondary'"
                                            :style="currentCalculated.fee_adjustments_data[item] === 0 && 'opacity:0.5'"
                                        >
                                            {{ formatNumber(currentCalculated.fee_adjustments_data[item]) }}</span
                                        >
                                    </div>
                                </div>
                            </template>
                        </div>
                        <div v-if="currentCalculated?.total_promotion_adjustments !== 0" class="d-flex" style="margin-top: 15px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                            <div style="width: 125px; font-size: 14px; font-weight: 400; line-height: 21px; white-space: nowrap">
                                <button class="btn btn-link text-secondary" style="padding: 0; font-size: 16px; font-weight: bold" @click="hiddenFields.refund_total_promo = !hiddenFields.refund_total_promo">
                                    <i class="bi" :class="hiddenFields.refund_total_promo ? 'bi-dash' : 'bi-plus'"></i>
                                </button>
                                {{ $t(`views.orders.detail.salesSummary.columns.refund_total_promo`) }}
                            </div>
                            <div class="d-flex align-items-center" style="gap: 5px">
                                <span style="font-size: 11.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.currency }}</span>
                                <span
                                    style="font-size: 11.9px; font-weight: 700; line-height: 17.85px; color: #00203f"
                                    :class="currentCalculated?.total_promotion_adjustments < 0 ? 'text-danger' : 'text-secondary'"
                                    :style="currentCalculated?.total_promotion_adjustments === 0 && 'opacity:0.5'"
                                >
                                    {{ formatNumber(currentCalculated?.total_promotion_adjustments) }}</span
                                >
                            </div>
                        </div>
                        <div v-if="hiddenFields.refund_total_promo">
                            <template v-for="item in Object.keys(currentCalculated.promotion_adjustments_data).sort()" :key="item">
                                <div class="d-flex" style="margin-top: 5px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                                    <div style="width: 125px; font-size: 12px; font-weight: 400; line-height: 21px; white-space: nowrap; padding-left: 22px">
                                        {{ $t(`views.orders.financeLabels.` + item) }}
                                    </div>
                                    <div class="d-flex align-items-center" style="gap: 5px">
                                        <span style="font-size: 10.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.currency }}</span>
                                        <span
                                            style="font-size: 10.9px; font-weight: 600; line-height: 17.85px; color: #00203f"
                                            :class="currentCalculated.promotion_adjustments_data[item] < 0 ? 'text-danger' : 'text-secondary'"
                                            :style="currentCalculated.promotion_adjustments_data[item] === 0 && 'opacity:0.5'"
                                        >
                                            {{ formatNumber(currentCalculated.promotion_adjustments_data[item]) }}</span
                                        >
                                    </div>
                                </div>
                            </template>
                        </div>
                        <div v-if="currentCalculated?.total_service_fees !== 0" class="d-flex" style="margin-top: 15px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                            <div style="width: 125px; font-size: 14px; font-weight: 400; line-height: 21px; white-space: nowrap">
                                <button class="btn btn-link text-secondary" style="padding: 0; font-size: 16px; font-weight: bold" @click="hiddenFields.total_service_fee = !hiddenFields.total_service_fee">
                                    <i class="bi" :class="hiddenFields.total_service_fee ? 'bi-dash' : 'bi-plus'"></i>
                                </button>
                                {{ $t(`views.orders.detail.salesSummary.columns.total_service_fee`) }}
                            </div>
                            <div class="d-flex align-items-center" style="gap: 5px">
                                <span style="font-size: 11.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.currency }}</span>
                                <span
                                    style="font-size: 11.9px; font-weight: 700; line-height: 17.85px; color: #00203f"
                                    :class="currentCalculated?.total_service_fees < 0 ? 'text-danger' : 'text-secondary'"
                                    :style="currentCalculated?.total_service_fees === 0 && 'opacity:0.5'"
                                >
                                    {{ formatNumber(currentCalculated?.total_service_fees) }}</span
                                >
                            </div>
                        </div>
                        <div v-if="hiddenFields.total_service_fee">
                            <template v-for="item in Object.keys(currentCalculated.service_fees_data).sort()" :key="item">
                                <div class="d-flex" style="margin-top: 5px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                                    <div style="width: 125px; font-size: 12px; font-weight: 400; line-height: 21px; white-space: nowrap; padding-left: 22px">
                                        {{ $t(`views.orders.financeLabels.` + item) }}
                                    </div>
                                    <div class="d-flex align-items-center" style="gap: 5px">
                                        <span style="font-size: 10.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.currency }}</span>
                                        <span
                                            style="font-size: 10.9px; font-weight: 600; line-height: 17.85px; color: #00203f"
                                            :class="currentCalculated.service_fees_data[item] < 0 ? 'text-danger' : 'text-secondary'"
                                            :style="currentCalculated.service_fees_data[item] === 0 && 'opacity:0.5'"
                                        >
                                            {{ formatNumber(currentCalculated.service_fees_data[item]) }}</span
                                        >
                                    </div>
                                </div>
                            </template>
                        </div>
                        <div v-if="currentCalculated?.total_removals !== 0" class="d-flex" style="margin-top: 15px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                            <div style="width: 125px; font-size: 14px; font-weight: 400; line-height: 21px; white-space: nowrap">
                                <button class="btn btn-link text-secondary" style="padding: 0; font-size: 16px; font-weight: bold" @click="hiddenFields.removal_total_final = !hiddenFields.removal_total_final">
                                    <i class="bi" :class="hiddenFields.removal_total_final ? 'bi-dash' : 'bi-plus'"></i>
                                </button>
                                {{ $t(`views.orders.detail.salesSummary.columns.removal_total_final`) }}
                            </div>
                            <div class="d-flex align-items-center" style="gap: 5px">
                                <span style="font-size: 11.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.currency }}</span>
                                <span
                                    style="font-size: 11.9px; font-weight: 700; line-height: 17.85px; color: #00203f"
                                    :class="currentCalculated?.total_removals < 0 ? 'text-danger' : 'text-secondary'"
                                    :style="currentCalculated?.total_removals === 0 && 'opacity:0.5'"
                                >
                                    {{ formatNumber(currentCalculated?.total_removals) }}</span
                                >
                            </div>
                        </div>
                        <div v-if="hiddenFields.removal_total_final">
                            <template v-for="item in Object.keys(currentCalculated.removals_data).sort()" :key="item">
                                <div class="d-flex" style="margin-top: 5px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                                    <div style="width: 125px; font-size: 12px; font-weight: 400; line-height: 21px; white-space: nowrap; padding-left: 22px">
                                        {{ $t(`views.orders.financeLabels.` + item) }}
                                    </div>
                                    <div class="d-flex align-items-center" style="gap: 5px">
                                        <span style="font-size: 10.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.currency }}</span>
                                        <span
                                            style="font-size: 10.9px; font-weight: 600; line-height: 17.85px; color: #00203f"
                                            :class="currentCalculated.removals_data[item] < 0 ? 'text-danger' : 'text-secondary'"
                                            :style="currentCalculated.removals_data[item] === 0 && 'opacity:0.5'"
                                        >
                                            {{ formatNumber(currentCalculated.removals_data[item]) }}</span
                                        >
                                    </div>
                                </div>
                            </template>
                        </div>
                        <div v-if="currentCalculated?.cost_of_goods_original_adjustments !== 0 && detail.statuses?.is_returned" class="d-flex" style="margin-top: 15px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                            <div style="padding-left: 22px; width: 125px; font-size: 14px; font-weight: 400; line-height: 21px; white-space: nowrap">
                                {{ $t(`views.orders.detail.salesSummary.columns.cost_of_goods`) }}
                                <InfoTooltip :content="$t(`views.orders.detail.salesSummary.columns.replacement_cog_tooltip`)" />
                            </div>
                            <div class="d-flex align-items-center" style="gap: 5px">
                                <span style="font-size: 11.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.sourceCurrency || currentCalculated?.currency }}</span>
                                <span
                                    style="font-size: 11.9px; font-weight: 700; line-height: 17.85px; color: #00203f"
                                    :class="currentCalculated?.cost_of_goods !== 0 ? 'text-danger' : 'text-secondary'"
                                    :style="currentCalculated?.cost_of_goods === 0 && 'opacity:0.5'"
                                >
                                    -{{ formatNumber(currentCalculated?.cost_of_goods) }}</span
                                >
                            </div>
                        </div>
                        <div v-if="currentCalculated?.total_refund !== 0" class="d-flex" style="margin-top: 15px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                            <div style="padding-left: 22px; width: 125px; font-size: 14px; font-weight: 400; line-height: 21px; white-space: nowrap">
                                {{ $t(`views.orders.detail.salesSummary.columns.refund_total`) }}
                            </div>
                            <div class="d-flex align-items-center" style="gap: 5px">
                                <span style="font-size: 11.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.currency }}</span>
                                <span
                                    style="font-size: 11.9px; font-weight: 700; line-height: 17.85px; color: #00203f"
                                    :class="currentCalculated?.total_refund < 0 ? 'text-danger' : 'text-secondary'"
                                    :style="currentCalculated?.total_refund === 0 && 'opacity:0.5'"
                                >
                                    {{ formatNumber(currentCalculated?.total_refund) }}</span
                                >
                            </div>
                        </div>
                        <div class="d-flex" style="margin-top: 15px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                            <div style="padding-left: 22px; width: 125px; font-size: 14px; font-weight: 400; line-height: 21px; white-space: nowrap">
                                {{ $t(`views.orders.detail.salesSummary.columns.${currentCalculated?.profit > 0 ? 'profit' : 'loss'}`) }}
                            </div>
                            <div class="d-flex align-items-center" style="gap: 5px">
                                <span style="font-size: 11.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ currentCalculated?.currency }}</span>
                                <span
                                    style="font-size: 11.9px; font-weight: 700; line-height: 17.85px; color: #00203f"
                                    :class="currentCalculated?.profit === 0 ? 'text-secondary' : currentCalculated?.profit > 0 ? 'text-success' : 'text-danger'"
                                    :style="currentCalculated?.profit === 0 && 'opacity:0.5'"
                                >
                                    {{ formatNumber(currentCalculated?.profit) }}</span
                                >
                            </div>
                        </div>
                        <div class="d-flex" style="margin-top: 15px; border-bottom: 0; border-color: transparent; justify-content: space-between">
                            <div style="padding-left: 22px; width: 125px; font-size: 14px; font-weight: 400; line-height: 21px; white-space: nowrap">
                                {{ $t(`views.orders.detail.salesSummary.columns.${currentCalculated?.profit > 0 ? 'roi' : 'loss_roi'}`) }}
                            </div>
                            <div class="d-flex align-items-right" :class="currentCalculated?.roi === 0 ? 'text-secondary' : currentCalculated?.roi > 0 ? 'text-success' : 'text-danger'" style="gap: 5px">
                                <i :class="currentCalculated?.roi > 0 ? 'bi-graph-up' : 'bi-graph-down'"></i>
                                <span style="font-size: 11.9px; font-weight: 700; line-height: 21.85px">{{ formatPercentage(currentCalculated?.roi) }}</span>
                            </div>
                        </div>
                    </div>
                    <!-- <div class="card-body" v-else>
                        {{ $t('views.orders.detail.salesSummary.pendingInfo') }}
                    </div> -->
                    <div v-if="detail?.calculated?.sourceCurrency !== detail?.calculated?.currency" class="card-footer d-flex" style="background-color: #f8fafd; padding-left: 40px; padding-right: 40px; justify-content: space-between">
                        <div style="width: 125px; font-size: 11.9px; font-weight: 400; line-height: 17.85px; color: #677788">{{ $t('views.orders.detail.salesSummary.footerText') }}</div>
                        <div style="font-size: 11.9px; font-weight: 700; line-height: 17.85px; color: #00203f">1 USD = {{ formatNumber(currentCalculated?.exchange_rate) }} {{ currentCalculated?.currency }}</div>
                    </div>
                </div>
                <!-- <div class="card">
                    <div class="card-body" style="padding: 37px 40px 21px 40px !important">
                        <div style="font-size: 14px; font-weight: 400; line-height: 21px; margin-bottom: 10px">Feedback İste</div>
                        <select class="form-select">
                            <option value="">Gönder</option>
                        </select>
                    </div>
                </div>
                <button class="btn btn-primary" style="width: 100%; margin-bottom: 30px">Mesaj Gönder</button> -->
            </div>
        </div>
    </div>
</template>

<script>
import CurrencyInput from '@/components/CurrencyInput.vue'
import ImagePlaceholder from '@/components/Inventory/ImagePlaceholder.vue'
import InventoryClipboard from '@/components/Inventory/InventoryClipboard.vue'
import InfoTooltip from '@/components/Variations/InfoTooltip.vue'
import { formatPercentage } from '@/utils/format-number'
import moment from 'moment'
import { mapActions, mapState } from 'vuex'

export default {
    name: 'OrdersDetail',
    components: { InventoryClipboard, CurrencyInput, ImagePlaceholder, InfoTooltip },
    data: function () {
        return {
            moment,
            formatPercentage,
            order_note: null,
            storeCOG: '',
            selectedItem: null,
            selectedSku: null,
            currentCalculated: null,
            hiddenFields: {
                sell_price: false,
                fees: false,
                promos: false,
                refund_price_total: false,
                total_refunded_fee: false,
                refund_total_promo: false,
                removal_total_final: false,
                total_service_fee: false,
            },
            salesSummary: ['sell_price', 'item_price', 'tax', 'amz_comission', 'fba_fee', 'fba_fee_tax', 'digital_service_fee', 'other_fee', 'cost_of_goods', 'recieved_price', 'profit', 'roi'],
        }
    },
    computed: {
        ...mapState('orders', ['detail']),
        buyerAddress() {
            if (this.detail.buyer?.address) {
                // eslint-disable-next-line no-unsafe-optional-chaining
                const { City, PostalCode, CountryCode, StateOrRegion } = this.detail.buyer?.address
                return `${City}, ${StateOrRegion}, ${PostalCode}, ${CountryCode}`
            }
            return null
        },
    },
    created() {
        this.getOrderDetail(this.$route.params.id).then(({ data }) => {
            this.order_note = data.order?.note
        })
    },
    watch: {
        detail: {
            handler(value) {
                if (value) {
                    this.currentCalculated = value.calculated
                }
            },
            deep: true,
            immediate: true,
        },
        selectedItem: {
            handler(value) {
                if (value === null) {
                    this.currentCalculated = this.detail.calculated
                    this.selectedSku = null
                } else {
                    this.currentCalculated = this.detail.items.find((item) => item.product.detail_id === value).calculated
                    this.selectedSku = this.detail.items.find((item) => item.product.detail_id === value).product.sku
                }
            },
            deep: true,
            immediate: true,
        },
    },
    methods: {
        ...mapActions('orders', ['getOrderDetail', 'setOrderNote', 'updateCog']),
        getStatusLabel(status) {
            const statuses = {
                Pending: this.$t('views.orders.statuses.Pending'),
                Unshipped: this.$t('views.orders.statuses.Unshipped'),
                PartiallyShipped: this.$t('views.orders.statuses.PartiallyShipped'),
                Shipped: this.$t('views.orders.statuses.Shipped'),
                Canceled: this.$t('views.orders.statuses.Canceled'),
                Unfulfillable: this.$t('views.orders.statuses.Unfulfillable'),
                InvoiceUncorfirmed: this.$t('views.orders.statuses.InvoiceUncorfirmed'),
                PendingAvailability: this.$t('views.orders.statuses.PendingAvailability'),
                Refunded: this.$t('views.orders.statuses.Refunded'),
                Replacement: this.$t('views.orders.statuses.Replacement'),
            }
            return statuses[status]
        },
        async updateCost(orderId, detailId, value, id) {
            if (this.storeCOG !== value) {
                await this.updateCog({ id: orderId, detailId, cost_of_goods: value })
                const timeout = setTimeout(async () => {
                    await this.getOrderDetail(id)
                    clearTimeout(timeout)
                }, 1000)
            }
        },
        handleChangeCurrentCalculated(detail_id) {
            if (this.selectedItem && this.selectedItem === detail_id) {
                this.selectedItem = null
            } else {
                this.selectedItem = detail_id
            }
        },
        formatNumber(number) {
            if (number === null || number === undefined) return null

            const factor = Math.pow(10, 2)
            const rounded = Math.floor(number * factor + 1e-9) / factor
            return rounded.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })
        },
    },
}
</script>

<style lang="scss" scoped>
.orders-detail {
    margin-top: 30px;

    &__page-header {
        border-bottom: 1px solid #e7eaf3;
        padding-bottom: 20px;
        margin-bottom: 30px;
    }

    h1 {
        font-size: 22px !important;
        font-weight: 500 !important;
        line-height: 21px;
        color: #00203f;
    }

    table {
        td {
            width: 180px;
            padding: 0.5rem 1rem;

            .status {
                color: #fff;
                font-size: 10.5px;
                font-weight: 700;
                line-height: 12.71px;
                padding: 3.67px 5.33px 3.67px 6.81px;
                border-radius: 5px;

                &.Pending {
                    background-color: #ffb53e;
                }
                &.Unshipped {
                    background-color: #00203f;
                }
                &.PartiallyShipped {
                    background-color: #00c9a7;
                }
                &.Shipped {
                    background-color: #00c9a7;
                }
                &.Canceled {
                    background-color: #00203f;
                }
                &.Unfulfillable {
                    background-color: #f5ca99;
                }
                &.InvoiceUncorfirmed {
                    background-color: #ed4c78;
                }
                &.PendingAvailability {
                    background-color: #ffb53e;
                }
                &.Refunded {
                    background-color: #ed4c78;
                }
                &.Replacement {
                    background-color: #677788;
                }
            }
        }
    }

    .text-ellipsis {
        font-size: 11.9px;
        font-weight: 400;
        line-height: 17.9px;
        color: #677788;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;

        &.title {
            max-width: 368px;
        }
    }

    .order-detail-item {
        display: flex;
        flex-direction: column;
        gap: 10px;
        font-size: 14px;
        font-weight: 400;
        line-height: 21px;
        margin-bottom: 20px;

        span:first-child {
            color: #00203f;
        }

        span:last-child {
            color: #677788;
        }
    }

    .card {
        &-header {
            padding: 37px 20px 21px 40px;
            font-size: 15px;
            font-weight: 500;
            line-height: 21px;
            color: #00203f;
        }
    }
}
</style>
